import React, { useEffect, useRef, useState } from 'react';
import {
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    Typography,
    useMediaQuery
} from '@material-ui/core';
import {
    AccountTree,
    AllInbox,
    Assignment,
    ArrowForward,
    Build,
    CallMerge,
    ChatBubbleOutline,
    Close as CloseIcon,
    CloudUpload,
    ContactSupport,
    Description,
    DirectionsCar,
    ErrorOutline,
    Event,
    ExitToApp as LoginIcon,
    Explore,
    FlashOn,
    Gavel,
    GetApp,
    Group,
    Help,
    HelpOutline,
    Layers,
    LinkedIn,
    Launch,
    LocationOn,
    Lock,
    Memory,
    Nature,
    Notifications,
    Phone,
    Search,
    Settings,
    ShowChart,
    Storage,
    Sync,
    TrendingUp,
    Twitter,
    VerifiedUser,
    LocalShipping,
    Apartment,
    Home
} from '@material-ui/icons';
import { useHistory } from 'react-router-dom/cjs/react-router-dom';
import mapDefinition from '../../../json/map/landingmap';
import { getEnvVariable } from '../../../modules/environmentalList';
import OwnerRegistration from '../../../modules/ownerregistration/OwnerRegistration';
import Map from '../../modules/map/map';
import { getLandingPagelogo } from '../header/layouts/ImageLogos';
import layoutRegistry from './layouts';
import HelpDrawer from './HelpDrawer';

const iconComponents = {
    accounttree: AccountTree,
    activity: ShowChart,
    alertcircle: ErrorOutline,
    allinbox: AllInbox,
    arrowforward: ArrowForward,
    arrowright: ArrowForward,
    assignment: Assignment,
    car: DirectionsCar,
    chatbubbleoutline: ChatBubbleOutline,
    contactsupport: ContactSupport,
    cpu: Memory,
    database: Storage,
    directionscar: DirectionsCar,
    download: GetApp,
    erroroutline: ErrorOutline,
    flashon: FlashOn,
    gavel: Gavel,
    getapp: GetApp,
    gitmerge: CallMerge,
    group: Group,
    help: Help,
    helpoutline: HelpOutline,
    layers: Layers,
    linkedin: LinkedIn,
    launch: Launch,
    locationon: LocationOn,
    login: LoginIcon,
    memory: Memory,
    messagesquare: ChatBubbleOutline,
    nature: Nature,
    network: AccountTree,
    package: AllInbox,
    phone: Phone,
    refreshcw: Sync,
    shieldcheck: VerifiedUser,
    showchart: ShowChart,
    storage: Storage,
    sync: Sync,
    trendingup: TrendingUp,
    twitter: Twitter,
    users: Group,
    verifieduser: VerifiedUser,
    build: Build,
    cloudupload: CloudUpload,
    description: Description,
    event: Event,
    explore: Explore,
    lock: Lock,
    notifications: Notifications,
    search: Search,
    settings: Settings,
    zap: FlashOn,
    localshipping: LocalShipping,
    apartment: Apartment,
    home: Home
};

const CardLoginScreen = () => {
    const history = useHistory();
    const mapRef = useRef(null);
    const isDesktop = useMediaQuery('(min-width:960px)');
    const [signup, setSignup] = useState(false);
    const [mapExpanded, setMapExpanded] = useState(false);
    const [config, setLandingConfig] = useState({});
    const [mapError, setMapError] = useState(false);
    const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
    const [hoveredHowItWorksIndex, setHoveredHowItWorksIndex] = useState(null);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [drawerUrl, setDrawerUrl] = useState('');
    const [drawerTitle, setDrawerTitle] = useState('FAQs');
    const [iframeLoading, setIframeLoading] = useState(false);
    const [iframeTimeout, setIframeTimeout] = useState(null);
    const [fullScreen, setFullScreen] = useState(false);
    const [helpContent, setHelpContent] = useState('');
    const [showBackButton, setShowBackButton] = useState(false);
    const [helpHistory, setHelpHistory] = useState([]);
    const isExternal = (getEnvVariable('AccessPortal') || '').toUpperCase() === 'E';
    const defaultAnimationSettings = {
        enabled: true,
        type: 'fade-up',
        startDelay: 0,
        stagger: 0.08,
        triggerOnce: true
    };

    useEffect(() => {
        if (window?.location?.hash?.includes('resetpassword')) {
            const locationPath = history.location.pathname || '';
            const lastSlash = locationPath.lastIndexOf('/');
            const identifier = locationPath.substring(lastSlash + 1);
            const redirectUrl = `${getEnvVariable('AuthorizationScreen')}forgot/resetpassword?guid=${identifier}`;
            window.location.replace(redirectUrl);
        }
    }, [history]);

    useEffect(() => {
        let isSubscribed = true;
        const fetchLandingConfig = async () => {
            try {
                const envFileName = getEnvVariable('LandingPageFileName');
                const basePath = process.env.PUBLIC_URL || '';
                const resolvedUrl = envFileName
                    ? envFileName.startsWith('http')
                        ? envFileName
                        : `${basePath}${envFileName}`
                    : `${basePath}/landing.json`;
                const cacheBustedUrl = resolvedUrl.includes('?')
                    ? `${resolvedUrl}&ts=${Date.now()}`
                    : `${resolvedUrl}?ts=${Date.now()}`;
                const response = await fetch(cacheBustedUrl);
                if (!response.ok) {
                    throw new Error('Unable to load landing configuration');
                }
                const loadedConfig = await response.json();
                if (isSubscribed) {
                    setLandingConfig((prev) => ({ ...prev, ...loadedConfig }));
                }
            } catch (error) {
                console.error('Failed to load landing configuration', error);
            }
        };

        fetchLandingConfig();
        return () => {
            isSubscribed = false;
        };
    }, []);

    useEffect(() => {
        if (!config.hero?.slideshow?.enabled || !config.hero?.slideshow?.images?.length) {
            return undefined;
        }

        const interval = setInterval(() => {
            setCurrentSlideIndex((previous) => {
                const total = config.hero?.slideshow?.images?.length || 1;
                return (previous + 1) % total;
            });
        }, config.hero?.slideshow?.interval || 6000);

        return () => clearInterval(interval);
    }, [config.hero?.slideshow?.enabled, config.hero?.slideshow?.images?.length, config.hero?.slideshow?.interval]);

    useEffect(() => {
        if (drawerOpen) {
            setIframeLoading(true);
            const timeout = setTimeout(() => {
                setIframeLoading(false);
            }, 10000); // 10 seconds
            setIframeTimeout(timeout);
        } else {
            setIframeLoading(false);
            if (iframeTimeout) clearTimeout(iframeTimeout);
        }
        return () => {
            if (iframeTimeout) clearTimeout(iframeTimeout);
        };
    }, [drawerOpen]);

    useEffect(() => {
        if (typeof window === 'undefined') {
            return undefined;
        }

        const elements = document.querySelectorAll('.reveal-item');
        if (!elements.length) {
            return undefined;
        }

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    const triggerOnce = entry.target.getAttribute('data-trigger-once') !== 'false';
                    if (entry.isIntersecting) {
                        entry.target.classList.add('reveal-active');
                        if (triggerOnce) {
                            observer.unobserve(entry.target);
                        }
                    } else if (!triggerOnce) {
                        entry.target.classList.remove('reveal-active');
                    }
                });
            },
            {
                threshold: 0.2,
                rootMargin: '0px 0px -10% 0px'
            }
        );

        elements.forEach((element) => observer.observe(element));

        return () => observer.disconnect();
    }, [config]);

    const getAnimationProps = (animationConfig = {}, index = 0) => {
        if (animationConfig === null) {
            return {};
        }

        const configOverrides = typeof animationConfig === 'object' ? animationConfig : {};
        const configWithDefaults = {
            ...defaultAnimationSettings,
            ...configOverrides
        };

        if (!configWithDefaults.enabled) {
            return {};
        }

        const hasExplicitDelay = typeof configWithDefaults.delay !== 'undefined';
        const computedDelay = hasExplicitDelay
            ? configWithDefaults.delay
            : (configWithDefaults.startDelay || 0) + index * (configWithDefaults.stagger || 0);

        const formattedDelay = typeof computedDelay === 'number'
            ? Number(computedDelay).toFixed(2)
            : computedDelay;

        const className = ['reveal-item', configWithDefaults.className]
            .filter(Boolean)
            .join(' ');

        const style = {
            ...(configWithDefaults.style || {}),
            transitionDelay:
                typeof computedDelay === 'number' ? `${computedDelay}s` : computedDelay
        };

        if (configWithDefaults.duration) {
            style.transitionDuration =
                typeof configWithDefaults.duration === 'number'
                    ? `${configWithDefaults.duration}s`
                    : configWithDefaults.duration;
        }

        return {
            className,
            dataAnim: configWithDefaults.type,
            dataDelay: formattedDelay,
            dataTriggerOnce: configWithDefaults.triggerOnce === false ? 'false' : 'true',
            style
        };
    };

    const applyHoverEffect = (event, hoverStyles = {}) => {
        if (!event?.currentTarget || !hoverStyles) {
            return;
        }
        Object.entries(hoverStyles).forEach(([styleKey, styleValue]) => {
            event.currentTarget.style[styleKey] = styleValue;
        });
    };

    const removeHoverEffect = (event, baseStyles = {}) => {
        if (!event?.currentTarget || !baseStyles) {
            return;
        }
        Object.entries(baseStyles).forEach(([styleKey, styleValue]) => {
            event.currentTarget.style[styleKey] = styleValue;
        });
    };

    const handleHelpUsClick = () => {
        const helpPath = isExternal ? getEnvVariable('ExternalHelp') : getEnvVariable('InternalHelp');
        const helpUrl = `${getEnvVariable('HelpUI')}${helpPath || ''}?view=embedded`;
        
        setDrawerTitle('FAQs');
        setDrawerUrl(helpUrl);
        setHelpContent('');
        setShowBackButton(false);
        setHelpHistory([]);
        setDrawerOpen(true);
    };

    const handleBackClick = () => {
        if (helpHistory.length > 0) {
            const previousContent = helpHistory[helpHistory.length - 1];
            setHelpContent(previousContent);
            setHelpHistory(helpHistory.slice(0, -1));
            
            if (helpHistory.length === 1) {
                setShowBackButton(false);
            }
        }
    };

    const handleContactUsClick = () => {
        const mailTo = getEnvVariable('LandingContactEmail') || 'apps.noreply@delasoft.com';
        window.open(`mailto:${mailTo}`);
    };

    const handleSignUpClick = () => {
        const registrationUrl = getEnvVariable('RegistrationPath');
        if (registrationUrl) {
            window.location.replace(registrationUrl);
        } else {
            setSignup(true);
        }
    };

    const handleClose = () => {
        setSignup(false);
    };

    const handleLoginClick = () => {
        try {
            localStorage.setItem('AccessPortal', getEnvVariable('AccessPortal'));
        } catch (error) {
            console.warn('Unable to persist AccessPortal selection', error);
        }

        const loginUrl = `${getEnvVariable('AuthorizationScreen')}login/default?usetype=${getEnvVariable('AccessPortal')}&ENV=${getEnvVariable('REACT_APP_PROFILE')}`;
        window.location.replace(loginUrl);
    };

    const handleExternalLogin = () => {
        const logins = getEnvVariable('AuthorizationLogins') || [];
        const externalEntry = logins.find(l => (l.AccessPortal || '').toUpperCase() === 'E');
        if (externalEntry && externalEntry.AuthorizationPath) {
            try {
                localStorage.setItem('AccessPortal', 'E');
            } catch (error) {
                console.warn('Unable to persist AccessPortal selection', error);
            }
            window.location.replace(externalEntry.AuthorizationPath);
        }
    };

    const handleStaffLogin = () => {
        const staffLoginUrl = config.staffLoginUrl || `${getEnvVariable('AuthorizationScreen')}login/default?usetype=I&ENV=${getEnvVariable('REACT_APP_PROFILE')}`;
        window.location.replace(staffLoginUrl);
    };

    const scrollToSection = (elementId) => {
        if (!elementId) {
            return;
        }
        const section = document.getElementById(elementId);
        section?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    const handleHeroButtonClick = (buttonConfig = {}) => {
        if (buttonConfig.action && functionMap[buttonConfig.action]) {
            functionMap[buttonConfig.action]();
            return;
        }
        if (buttonConfig.href) {
            window.open(buttonConfig.href, buttonConfig.target || '_blank', 'noopener');
        }
    };

    const getIcon = (iconName, iconProps = {}) => {
        if (!iconName) {
            return null;
        }
        const normalized = iconName.toLowerCase();
        const IconComponent = iconComponents[normalized];
        return IconComponent ? <IconComponent {...iconProps} /> : null;
    };

    const functionMap = {
        handleHelpUsClick,
        handleContactUsClick,
        handleSignUpClick,
        handleLoginClick,
        handleExternalLogin,
        handleScrollToHero: () => scrollToSection('hero-section'),
        handleScrollToInsights: () => scrollToSection('insights-section'),
        handleScrollToFaq: () => scrollToSection('insights-section'),
        handleScrollToChallenges: () => scrollToSection('challenges-section'),
        handleScrollToSolutions: () => scrollToSection('benefits-section'),
        handleScrollToBenefits: () => scrollToSection('benefits-section'),
        handleScrollToHowItWorks: () => scrollToSection('howitworks-section'),
        handleScrollToStatistics: () => scrollToSection('statistics-section'),
        handleScrollToResults: () => scrollToSection('statistics-section'),
        handleScrollToImplementation: () => scrollToSection('implementation-section'),
        handleScrollToFooter: () => scrollToSection('footer-section'),
        handleScrollToPermits: () => scrollToSection('permits-section'),
        handleStaffLogin,
        scrollToHero: () => scrollToSection('hero-section'),
        scrollToBenefits: () => scrollToSection('benefits-section'),
        scrollToStatistics: () => scrollToSection('statistics-section'),
        scrollToFooter: () => scrollToSection('footer-section')
    };

    const sharedLayoutContext = {
        config,
        isDesktop,
        isExternal,
        ...( !isExternal &&  {
            map: mapDefinition,
            MapComponent: Map,
            mapRef,
            mapExpanded,
            setMapExpanded,
            mapError,
            setMapError
        }),
        currentSlideIndex,
        setCurrentSlideIndex,
        hoveredHowItWorksIndex,
        setHoveredHowItWorksIndex,
        functionMap,
        applyHoverEffect,
        removeHoverEffect,
        handleHeroButtonClick,
        handleSignUpClick,
        handleLoginClick,
        getIcon,
        getLandingPagelogo,
        getAnimationProps
    };

    const resolveLayoutComponent = (sectionKey, sectionConfig) => {
        const registryEntry = layoutRegistry?.[sectionKey];
        if (!registryEntry) {
            return null;
        }

        if (sectionConfig?.layoutCategory && registryEntry[sectionConfig.layoutCategory]) {
            return registryEntry[sectionConfig.layoutCategory];
        }

        return registryEntry.default || null;
    };

    const renderSection = (sectionKey, sectionConfig) => {
        if (!sectionConfig) {
            return null;
        }

        const SectionComponent = resolveLayoutComponent(sectionKey, sectionConfig);
        if (!SectionComponent) {
            return null;
        }

        return (
            <SectionComponent
                key={`${sectionKey}-section`}
                sectionConfig={sectionConfig}
                layoutContext={sharedLayoutContext}
                globalConfig={config}
            />
        );
    };

    const insightsPlacement = config.insights?.placement || 'beforeBenefits';

    return (
        <div style={{ display: 'flex', minHeight: '100vh', position: 'relative' }}>
            {/* Main Content Area */}
            <div style={{
                ...config.layout?.styles,
                flex: 1,
                marginRight: drawerOpen && !fullScreen ? '45vw' : '0',
                transition: 'margin-right 0.3s ease',
                overflow: 'visible'
            }}>
                {config.displayHeader && renderSection('header', config.header)}

                {isExternal && config.displayBanner && config.banner?.display && renderSection('banner', config.banner)}

                {config.displayHero && renderSection('hero', config.hero)}

                {config.insights && insightsPlacement !== 'afterBenefits' && renderSection('insights', config.insights)}

                {config.challenges?.items?.length > 0 && renderSection('challenges', config.challenges)}

                {config.displayBenefits && config.benefits?.items?.length > 0 && renderSection('benefits', config.benefits)}

                {config.insights && insightsPlacement === 'afterBenefits' && renderSection('insights', config.insights)}

                {config.howitworks?.items?.length > 0 && renderSection('howitworks', config.howitworks)}

                {config.keybenefits?.items?.length > 0 && renderSection('keybenefits', config.keybenefits)}

                {config.displayStatistics && config.statistics?.items?.length > 0 && renderSection('statistics', config.statistics)}

                {config.implementation?.items?.length > 0 && renderSection('implementation', config.implementation)}

                {config.cta && renderSection('cta', config.cta)}

                {config.displayPermits && config.permits?.items?.length > 0 && renderSection('permits', config.permits)}

                {config.displayFooter && config.footer && renderSection('footer', config.footer)}

                {signup && (
                <Dialog
                    fullScreen={config.dialog?.fullScreen}
                    open={signup}
                    onClose={handleClose}
                    style={config.dialog?.styles || {}}
                >
                    <DialogTitle>
                        <Typography
                            color={config.dialog?.titleStyles?.color || 'primary'}
                            variant={config.dialog?.typography?.variant || 'h6'}
                            style={config.dialog?.titleStyles || {}}
                        >
                            Create an Account
                        </Typography>
                        <IconButton
                            aria-label={config.dialog?.iconButton?.ariaLabel || 'close'}
                            onClick={handleClose}
                            color={config.dialog?.iconButton?.color || 'secondary'}
                            style={config.dialog?.closeButtonStyles || {}}
                        >
                            <CloseIcon />
                        </IconButton>
                    </DialogTitle>
                    <DialogContent>
                        <OwnerRegistration />
                    </DialogContent>
                </Dialog>
            )}
            </div>

            <HelpDrawer
                open={drawerOpen}
                onClose={() => setDrawerOpen(false)}
                title={drawerTitle}
                url={drawerUrl}
                content={helpContent}
                loading={iframeLoading}
                onLoadComplete={() => {
                    setIframeLoading(false);
                    if (iframeTimeout) clearTimeout(iframeTimeout);
                }}
                fullScreen={fullScreen}
                onToggleFullScreen={() => setFullScreen((prev) => !prev)}
                showBackButton={showBackButton}
                onBackClick={handleBackClick}
            />
        </div>
    );
};

export default CardLoginScreen;
